import { Flex, Modal, type ModalProps } from "antd";
import { ModalSemanticStyles } from "antd/es/modal";
import { CSSProperties, type FC, type ReactNode } from "react";

import BaseButton from "@/shared/ui/BaseButton";
import { SecondaryText } from "@/shared/ui/Typography";

import { StyledTitle } from "./Styled";

export interface BaseModalProps extends ModalProps {
    onClose: () => void;
    onConfirm?: () => void | Promise<void>;

    title?: ReactNode;
    description?: ReactNode;
    children?: ReactNode;

    confirmText?: string;
    cancelText?: string;
    confirmLoading?: boolean;
    confirmDisabled?: boolean;
    danger?: boolean;
    hideCancel?: boolean;
    hideConfirm?: boolean;

    icon?: ReactNode;

    actions?: ReactNode | null;
    titleStyle?: CSSProperties;
    modalStyles?: ModalSemanticStyles;
}

export const BaseModal: FC<BaseModalProps> = ({
    onClose,
    onConfirm,

    title = "",
    description,
    children,

    confirmText = "Підтвердити",
    cancelText = "Скасувати",
    confirmLoading = false,
    confirmDisabled = false,
    danger = false,
    hideCancel = false,
    hideConfirm = false,

    icon,
    actions,

    open,
    centered = true,
    titleStyle,
    modalStyles,

    ...rest
}) => {
    const handleConfirm = () => {
        onConfirm?.();
    };

    const header = title && (
        <Flex align="center" gap="8px">
            {!!icon && icon}

            <StyledTitle style={titleStyle}>{title}</StyledTitle>
        </Flex>
    );

    const defaultActions =
        !hideCancel || !hideConfirm ? (
            <Flex vertical gap="8px">
                {!hideConfirm && (
                    <BaseButton
                        type="primary"
                        onClick={handleConfirm}
                        loading={confirmLoading}
                        danger={danger}
                        disabled={confirmDisabled}
                    >
                        {confirmText}
                    </BaseButton>
                )}

                {!hideCancel && (
                    <BaseButton
                        type="default"
                        onClick={onClose}
                        disabled={confirmLoading}
                    >
                        {cancelText}
                    </BaseButton>
                )}
            </Flex>
        ) : null;

    return (
        <Modal
            title=""
            open={open}
            onCancel={onClose}
            centered={centered}
            footer={null}
            styles={{
                mask: {
                    backgroundColor: "rgba(0, 0, 0, 0.2)",
                    backdropFilter: "blur(10px)",
                    WebkitBackdropFilter: "blur(10px)",
                    ...modalStyles?.mask,
                },
                container: {
                    maxHeight: "95vh",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    padding: "20px 0",
                    ...modalStyles?.container,
                },
                body: {
                    flexGrow: 1,
                    height: "100%",
                    overflowY: "auto",
                    padding: "0 24px",
                    display: "flex",
                    flexDirection: "column",
                    ...modalStyles?.body,
                },
            }}
            {...rest}
        >
            <Flex vertical gap="16px" style={{ minHeight: 0 }}>
                {header}

                {description && <SecondaryText>{description}</SecondaryText>}

                {children}

                {actions === undefined ? defaultActions : actions}
            </Flex>
        </Modal>
    );
};
